import * as React from 'react';
import { Portal } from '@radix-ui/react-portal';
import { Presence } from '@radix-ui/react-presence';
import { FocusScope } from '@radix-ui/react-focus-scope';
import { DismissableLayer } from '@radix-ui/react-dismissable-layer';
// What Radix's modal Dialog uses to hide the page behind it: ref-counted, so stacked and nested modals (Modal inside a
// Radix Dialog, two Modals) can open and close in any order, and live regions (the Toaster) are left alone. It is a
// dependency of @radix-ui/react-dialog (hoisted in the lockfile); this file does not declare it itself.
import { hideOthers } from 'aria-hidden';

export interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  /** Classes for the content panel — position, size, and the `data-[state]` enter/exit animation. */
  className?: string;
  /** Classes for the scrim/overlay — usually `fixed inset-0 bg-black/50` + a `data-[state]` fade. */
  overlayClassName?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
}

/**
 * Sticky-safe modal primitive.
 *
 * Composes Radix's FocusScope (focus trap), DismissableLayer (Esc / click-outside), Presence
 * (enter/exit animations via `data-[state]` classes) and Portal — i.e. everything Radix's modal
 * Dialog gives — EXCEPT `react-remove-scroll`. Radix's modal Dialog locks background scroll by
 * setting `overflow: hidden` on `<body>`, which turns body into a scroll container and breaks the
 * app's `position: sticky` header/rail (see the `overflow-x: clip` note in index.css). Instead we
 * lock scroll with a wheel/touchmove event-swallow that never touches the body — so sticky keeps
 * working. Inside the panel, the consumer's own `overflow-y-auto` + `overscroll-contain` scrolls
 * normally.
 *
 * It is a true modal: focus trapped and returned to the opener (FocusScope), Escape closes (DismissableLayer), the panel
 * is labelled (`aria-label` / `aria-labelledby`), and while it is open the rest of the page is aria-hidden (`hideOthers`,
 * as Radix's modal Dialog does, without touching <body>'s style). The page behind cannot be reached otherwise: the
 * scrim covers it and the focus scope holds focus in the panel. The portal host exists only while the modal is open
 * (or finishing its exit), so a closed Modal never sits in <body> to be hidden by, or to hide, another one.
 */
/**
 * Every open Modal's panel. The page is hidden once for the whole set (`hideOthers` takes several targets), so Modals
 * opened in the same tick, stacked, or closed in any order never hide one another's panel, and the page is restored
 * only when the last one is gone.
 */
const openPanels = new Set<HTMLElement>();
let undoHide: (() => void) | null = null;
function refreshHidden() {
  if (undoHide) undoHide();
  undoHide = openPanels.size > 0 ? hideOthers(Array.from(openPanels)) : null;
}

export function Modal({
  open,
  onOpenChange,
  children,
  className,
  overlayClassName,
  ...aria
}: ModalProps) {
  const panelRef = React.useRef<HTMLDivElement | null>(null);
  // The panel mounts a render after `open` turns true (Radix Portal), so the node is state, not just a ref.
  const [panelNode, setPanelNode] = React.useState<HTMLDivElement | null>(null);
  // Mounted while open and until the panel has finished leaving (its ref clears), then the portal host goes too.
  const [mounted, setMounted] = React.useState(open);
  const openRef = React.useRef(open);
  openRef.current = open;
  React.useEffect(() => {
    if (open) setMounted(true);
  }, [open]);
  const setPanel = React.useCallback((node: HTMLDivElement | null) => {
    panelRef.current = node;
    setPanelNode(node);
    if (!node && !openRef.current) setMounted(false);
  }, []);

  // The rest of the page is hidden from assistive technology while the panel is up (ref-counted: any close order).
  React.useEffect(() => {
    if (!open || !panelNode) return;
    openPanels.add(panelNode);
    refreshHidden();
    return () => {
      openPanels.delete(panelNode);
      refreshHidden();
    };
  }, [open, panelNode]);

  // Lock background scroll without mutating <body> (which would break sticky). A gesture is allowed
  // only when a scrollable element *inside the panel* can actually consume it in that direction —
  // otherwise it's swallowed, so it can never chain out to the page (a wheel over the panel's
  // non-scrolling chrome, or past a scroll region's edge, is blocked).
  React.useEffect(() => {
    if (!open) return;
    const canConsume = (target: EventTarget | null, deltaY: number): boolean => {
      const panel = panelRef.current;
      if (!panel || !(target instanceof Node) || !panel.contains(target)) return false;
      let el: Element | null = target instanceof Element ? target : target.parentElement;
      while (el && panel.contains(el)) {
        const oy = getComputedStyle(el).overflowY;
        if ((oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight) {
          if (deltaY < 0) return el.scrollTop > 0;                                   // scrolling up
          if (deltaY > 0) return el.scrollTop + el.clientHeight < el.scrollHeight - 1; // scrolling down
          return true; // touch (deltaY 0) — overscroll-contain handles the edges
        }
        el = el.parentElement;
      }
      return false;
    };
    const onWheel = (e: WheelEvent) => { if (!canConsume(e.target, e.deltaY)) e.preventDefault(); };
    const onTouchMove = (e: TouchEvent) => { if (!canConsume(e.target, 0)) e.preventDefault(); };
    document.addEventListener('wheel', onWheel, { passive: false });
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    return () => {
      document.removeEventListener('wheel', onWheel);
      document.removeEventListener('touchmove', onTouchMove);
    };
  }, [open]);

  const state = open ? 'open' : 'closed';
  if (!open && !mounted) return null;

  return (
    <Portal>
      <Presence present={open}>
        <div data-state={state} className={overlayClassName} aria-hidden />
      </Presence>
      <Presence present={open}>
        <FocusScope asChild loop trapped>
          <DismissableLayer
            asChild
            onEscapeKeyDown={() => onOpenChange(false)}
            onPointerDownOutside={() => onOpenChange(false)}
            onFocusOutside={(e) => e.preventDefault()}
          >
            <div
              ref={setPanel}
              role="dialog"
              aria-modal="true"
              data-state={state}
              className={className}
              {...aria}
            >
              {children}
            </div>
          </DismissableLayer>
        </FocusScope>
      </Presence>
    </Portal>
  );
}

export default Modal;
